import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2, Sparkles } from 'lucide-react';
interface AvatarSelectorProps {
  selectedAvatar: string;
  onSelect: (avatarUrl: string) => void;
}
export function AvatarSelector({
  selectedAvatar,
  onSelect
}: AvatarSelectorProps) {
  const [avatars, setAvatars] = useState<string[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Fallback avatars in case generation fails
  const fallbackAvatars = ['https://api.dicebear.com/7.x/avataaars/svg?seed=Felix', 'https://api.dicebear.com/7.x/avataaars/svg?seed=Aneka', 'https://api.dicebear.com/7.x/avataaars/svg?seed=Jasmine', 'https://api.dicebear.com/7.x/avataaars/svg?seed=Oliver', 'https://api.dicebear.com/7.x/avataaars/svg?seed=Luna', 'https://api.dicebear.com/7.x/avataaars/svg?seed=Max', 'https://api.dicebear.com/7.x/avataaars/svg?seed=Bella', 'https://api.dicebear.com/7.x/avataaars/svg?seed=Charlie', 'https://api.dicebear.com/7.x/avataaars/svg?seed=Sophie', 'https://api.dicebear.com/7.x/avataaars/svg?seed=Leo'];
  useEffect(() => {
    generateAvatars();
  }, []);
  const generateAvatars = async () => {
    setIsGenerating(true);
    setError(null);
    try {
      // Avatar style prompts for diversity
      const prompts = ['professional avatar, friendly smile, business casual, warm lighting', 'creative avatar, artistic style, colorful background, modern aesthetic', 'minimalist avatar, clean design, neutral tones, simple background', 'tech professional avatar, glasses, confident expression, blue tones', 'casual avatar, relaxed style, outdoor setting, natural lighting', 'elegant avatar, sophisticated look, formal attire, studio lighting', 'sporty avatar, athletic appearance, energetic vibe, bright colors', 'artistic avatar, creative expression, unique style, vibrant colors', 'friendly avatar, approachable smile, casual clothing, soft lighting', 'professional avatar, confident pose, modern style, clean background'];
      const generatedAvatars: string[] = [];
      // Generate avatars using OpenAI DALL-E
      for (let i = 0; i < 10; i++) {
        try {
          const response = await fetch('https://requests.magicpatterns.com/openai/proxy', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              model: 'dall-e-3',
              prompt: `Create a simple, clean profile avatar illustration: ${prompts[i]}. Style: minimal, friendly, professional. Square format, centered portrait.`,
              n: 1,
              size: '1024x1024',
              quality: 'standard'
            })
          });
          if (!response.ok) {
            throw new Error('Failed to generate avatar');
          }
          const data = await response.json();
          if (data.data && data.data[0] && data.data[0].url) {
            generatedAvatars.push(data.data[0].url);
          } else {
            generatedAvatars.push(fallbackAvatars[i]);
          }
        } catch (err) {
          // Use fallback for this avatar
          generatedAvatars.push(fallbackAvatars[i]);
        }
      }
      setAvatars(generatedAvatars);
    } catch (err) {
      console.error('Error generating avatars:', err);
      setError('Using default avatars');
      setAvatars(fallbackAvatars);
    } finally {
      setIsGenerating(false);
    }
  };
  return <div className="space-y-4" data-id="element-120">
      <div className="flex items-center justify-between" data-id="element-121">
        <label className="block text-sm font-medium text-charcoal" data-id="element-122">
          Choose Avatar
        </label>
        {isGenerating && <div className="flex items-center gap-2 text-xs text-moss-gray" data-id="element-123">
            <Loader2 size={14} className="animate-spin" data-id="element-124" />
            <span data-id="element-125">Generating...</span>
          </div>}
      </div>

      {error && <div className="text-xs text-moss-gray flex items-center gap-1" data-id="element-126">
          <Sparkles size={12} data-id="element-127" />
          <span data-id="element-128">{error}</span>
        </div>}

      <div className="grid grid-cols-5 gap-3" data-id="element-129">
        {(avatars.length > 0 ? avatars : fallbackAvatars).map((avatar, index) => <motion.button key={index} whileTap={{
        scale: 0.95
      }} onClick={() => onSelect(avatar)} className={`aspect-square rounded-full overflow-hidden border-2 transition-all ${selectedAvatar === avatar ? 'border-dark-green ring-2 ring-dark-green/20' : 'border-sand hover:border-dark-green/50'}`} data-id="element-130">
              {isGenerating && avatars.length === 0 ? <div className="w-full h-full bg-sand/30 flex items-center justify-center" data-id="element-131">
                  <Loader2 size={16} className="animate-spin text-moss-gray" data-id="element-132" />
                </div> : <img src={avatar} alt={`Avatar ${index + 1}`} className="w-full h-full object-cover" data-id="element-133" />}
            </motion.button>)}
      </div>
    </div>;
}